import React from 'react';
import InvoiceStatusBadge from './InvoiceStatusBadge';
import { formatCurrencyAs } from '../../../utils/currency';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import InvoiceActionsMenu from './InvoiceActionsMenu';




const InvoiceTable = ({
  invoices, 
  selectedInvoices, 
  onSelectInvoice, 
  onSelectAll,
  sortConfig,
  onSort,
  onView,
  onEdit,
  onDuplicate,
  onSend,
  onApprove,
  onDelete,
  onPreview,
  onSendEmail,
  onMarkPaid,
  onDownloadPDF
}) => {
  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) {
      return <Icon name="ChevronsUpDown" size={16} className="text-muted-foreground" />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={16} className="text-primary" />
      : <Icon name="ChevronDown" size={16} className="text-primary" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysOverdue = (dueDate, status) => {
    if (status !== 'Overdue') return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left w-12">
                <Checkbox
                  checked={selectedInvoices?.length === invoices?.length && invoices?.length > 0}
                  onChange={onSelectAll}
                  indeterminate={selectedInvoices?.length > 0 && selectedInvoices?.length < invoices?.length}
                />
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('invoiceNumber')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Invoice #</span>
                  {getSortIcon('invoiceNumber')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('customer')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Customer</span>
                  {getSortIcon('customer')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('amount')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Amount</span>
                  {getSortIcon('amount')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('issueDate')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Issue Date</span>
                  {getSortIcon('issueDate')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('dueDate')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Due Date</span>
                  {getSortIcon('dueDate')}
                </div>
              </th>
              <th 
                className="px-4 py-3 text-left cursor-pointer hover:bg-muted transition-smooth"
                onClick={() => onSort('status')}
              >
                <div className="flex items-center gap-2 font-heading font-semibold text-sm text-foreground">
                  <span>Status</span>
                  {getSortIcon('status')}
                </div>
              </th>
              <th className="px-4 py-3 text-right font-heading font-semibold text-sm text-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {invoices?.map((invoice) => {
              const daysOverdue = getDaysOverdue(invoice?.dueDate, invoice?.status);
              return (
                <tr 
                  key={invoice?.id} 
                  className="hover:bg-muted/30 transition-smooth"
                >
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={selectedInvoices?.includes(invoice?.id)}
                      onChange={() => onSelectInvoice(invoice?.id)}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <span className="data-text text-primary font-medium">{invoice?.invoiceNumber}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="font-body font-medium text-sm text-foreground">{invoice?.customerName || invoice?.customer}</span>
                      <span className="caption text-muted-foreground">{invoice?.customerEmail}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="data-text font-semibold text-foreground">
                        {formatCurrencyAs(invoice?.amount, invoice?.currency)}
                      </span>
                      {invoice?.paidAmount > 0 && invoice?.status === 'Partial' && (
                        <span className="caption text-success">
                          {formatCurrencyAs(invoice?.paidAmount, invoice?.currency)} paid
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className="caption text-muted-foreground">{formatDate(invoice?.issueDate)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-col">
                      <span className="caption text-muted-foreground">{formatDate(invoice?.dueDate)}</span>
                      {daysOverdue && (
                        <span className="caption text-error font-medium">
                          {daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <InvoiceStatusBadge status={invoice?.status} />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <InvoiceActionsMenu
                      invoice={invoice}
                      onView={onView}
                      onEdit={onEdit}
                      onDuplicate={onDuplicate}
                      onSend={onSend}
                      onApprove={onApprove}
                      onDelete={onDelete}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InvoiceTable;